import { useState, useCallback, useEffect, useMemo } from 'react';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';

/**
 * Custom hook for managing resizable and sortable table columns
 * @param {string} storageKey - Unique key for localStorage (e.g., 'vehicles-table', 'equipment-table')
 * @param {Array} columns - Array of column definitions
 * @param {Array} dataSource - Array of data to sort
 * @returns {Object} { sortedData, enhancedColumns, handleTableChange }
 */
const useResizableSortableTable = (storageKey, columns, dataSource) => {
  // Load saved column widths from localStorage
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`${storageKey}-widths`);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Failed to load column widths:', error);
      return {};
    }
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(`${storageKey}-sort`);
      return saved ? JSON.parse(saved) : { field: null, order: null };
    } catch (error) {
      console.error('Failed to load sort config:', error);
      return { field: null, order: null };
    }
  });

  // Save column widths to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}-widths`, JSON.stringify(columnWidths));
    } catch (error) {
      console.error('Failed to save column widths:', error);
    }
  }, [columnWidths, storageKey]);

  // Save sort config to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}-sort`, JSON.stringify(sortConfig));
    } catch (error) {
      console.error('Failed to save sort config:', error);
    }
  }, [sortConfig, storageKey]);

  // Create ResizableTitle component for column headers
  const ResizableTitle = useCallback((props) => {
    const { onResize, width, ...restProps } = props;

    if (!width) {
      return <th {...restProps} />;
    }

    return (
      <th
        {...restProps}
        style={{ ...restProps.style, position: 'relative' }}
      >
        {restProps.children}
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 5,
            cursor: 'col-resize',
            zIndex: 1,
            userSelect: 'none',
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.pageX;
            const startWidth = width;

            const handleMouseMove = (moveEvent) => {
              const deltaX = moveEvent.pageX - startX;
              const newWidth = Math.max(50, startWidth + deltaX);
              onResize(newWidth);
            };

            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        />
      </th>
    );
  }, []);

  // Handle column resize
  const handleResize = useCallback((key) => (newWidth) => {
    setColumnWidths((prev) => ({
      ...prev,
      [key]: newWidth,
    }));
  }, []);

  // Sort comparison functions
  const compareFunctions = {
    text: (a, b, field) => {
      const aVal = (a[field] || '').toString().toLowerCase();
      const bVal = (b[field] || '').toString().toLowerCase();
      return aVal.localeCompare(bVal);
    },
    number: (a, b, field) => {
      const aVal = parseFloat(a[field]) || 0;
      const bVal = parseFloat(b[field]) || 0;
      return aVal - bVal;
    },
    date: (a, b, field) => {
      const aVal = a[field] ? new Date(a[field]).getTime() : 0;
      const bVal = b[field] ? new Date(b[field]).getTime() : 0;
      return aVal - bVal;
    },
  };

  // Sort data based on current sort config
  const sortedData = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order || !dataSource) {
      return dataSource || [];
    }

    const column = columns.find(col => col.key === sortConfig.field || col.dataIndex === sortConfig.field);
    if (!column) {
      return dataSource || [];
    }

    const field = column.dataIndex || column.key;
    const compareType = column.sortType || 'text';
    const compareFunc = compareFunctions[compareType];

    const sorted = [...dataSource].sort((a, b) => {
      const result = compareFunc(a, b, field);
      return sortConfig.order === 'ascend' ? result : -result;
    });

    return sorted;
  }, [dataSource, sortConfig, columns]);

  // Handle sort click on column header
  const handleSort = useCallback((field) => {
    setSortConfig((prev) => {
      if (prev.field !== field) {
        return { field, order: 'ascend' };
      }
      if (prev.order === 'ascend') {
        return { field, order: 'descend' };
      }
      return { field: null, order: null };
    });
  }, []);

  // Enhance columns with resize and sort capabilities
  const enhancedColumns = useMemo(() => {
    return columns.map((col) => {
      const key = col.key || col.dataIndex;
      const width = columnWidths[key] || col.width;

      // Create title with sort indicator
      const isSorted = sortConfig.field === key || sortConfig.field === col.dataIndex;
      const sortOrder = isSorted ? sortConfig.order : null;

      const titleWithSort = (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => handleSort(key)}
        >
          <span>{col.title}</span>
          <span style={{ marginLeft: 4, display: 'flex', flexDirection: 'column', fontSize: 10 }}>
            <CaretUpOutlined
              style={{
                color: sortOrder === 'ascend' ? '#1890ff' : '#bfbfbf',
                marginBottom: -4,
              }}
            />
            <CaretDownOutlined
              style={{
                color: sortOrder === 'descend' ? '#1890ff' : '#bfbfbf',
              }}
            />
          </span>
        </div>
      );

      return {
        ...col,
        width,
        title: col.sortable !== false ? titleWithSort : col.title,
        onHeaderCell: (column) => ({
          width: column.width,
          onResize: handleResize(key),
        }),
      };
    });
  }, [columns, columnWidths, sortConfig, handleResize, handleSort]);

  // Antd Table onChange handler (for compatibility)
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    // This can be used if needed for additional table change handling
  }, []);

  return {
    sortedData,
    enhancedColumns,
    handleTableChange,
    ResizableTitle,
  };
};

export default useResizableSortableTable;
